import { Controller } from "@hotwired/stimulus"
import { observedFetch, currentLevel } from "../field_network_health"
import { timeoutFor } from "../field_timeout_policy"
import { logError } from "../field_logger"

// Handles the bad connectivity toggle on the field place details page.
// Shows a confirmation dialog before saving so the Tasker understands the effect.
//
// Values:
//   placeId  -- the Place#id
//   flagged  -- current bad_connectivity boolean
//
// Targets:
//   dialog   -- the <dialog> confirmation element
//   badge    -- the status badge shown on the details page

export default class extends Controller {
  static values  = { placeId: Number, flagged: Boolean }
  static targets = ["dialog", "badge", "flagContent", "clearContent", "confirmBtn"]

  // Fires automatically whenever flaggedValue changes (including initial load)
  flaggedValueChanged() {
    this._applyBadge()
    this._applyDialog()
  }

  // Open the confirmation dialog instead of toggling immediately
  requestToggle() {
    this.dialogTarget.showModal()
  }

  // User confirmed -- close dialog and commit
  async confirm() {
    this.dialogTarget.close()
    await this._doToggle()
  }

  // User cancelled -- close dialog, no change
  cancel() {
    this.dialogTarget.close()
  }

  // -- private --

  async _doToggle() {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    // Optimistic update -- flaggedValueChanged fires automatically
    this.flaggedValue = !this.flaggedValue

    try {
      const response = await observedFetch(`/api/v1/field/places/${this.placeIdValue}/toggle_bad_connectivity`, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        signal: AbortSignal.timeout(timeoutFor("toggle_write", currentLevel()))
      }, { kind: "json_write", sizeClass: "small" })

      if (!response.ok) throw new Error(`Server error: ${response.status}`)

      const data = await response.json()
      this.flaggedValue = data.bad_connectivity
    } catch (error) {
      console.error("Bad connectivity toggle failed:", error)
      // A manual, user-triggered write with no automatic retry -- this is the
      // terminal outcome of this attempt, not a "will retry next cycle"
      // condition, so it logs at error level (semantics table,
      // docs/dev_patterns/pwa-diagnostics-telemetry.md).
      logError("app", "Bad connectivity toggle failed", {
        placeId: this.placeIdValue, message: error?.message || String(error)
      }).catch(() => {})
      // Roll back optimistic update
      this.flaggedValue = !this.flaggedValue
    }
  }

  _applyBadge() {
    if (!this.hasBadgeTarget) return
    const badge = this.badgeTarget
    if (this.flaggedValue) {
      badge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m3 3 8.735 8.735m0 0a.374.374 0 1 1 .53.53m-.53-.53.53.53m0 0L21 21M14.652 9.348a3.75 3.75 0 0 1 0 5.304m2.121-7.425a6.75 6.75 0 0 1 0 9.546m2.121-11.667c3.808 3.807 3.808 9.98 0 13.788m-9.546-4.242a3.733 3.733 0 0 1-1.06-2.122m-1.061 4.243a6.75 6.75 0 0 1-1.625-6.929m-.496 9.05c-3.068-3.067-3.664-7.67-1.79-11.334M12 12h.008v.008H12V12Z"/></svg>Poor signal flagged`
      badge.className = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700"
    } else {
      badge.textContent = "Signal OK"
      badge.className = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-500"
    }
  }

  _applyDialog() {
    const flagged = this.flaggedValue
    if (this.hasFlagContentTarget) this.flagContentTarget.classList.toggle("hidden", flagged)
    if (this.hasClearContentTarget) this.clearContentTarget.classList.toggle("hidden", !flagged)
    if (this.hasConfirmBtnTarget) {
      const btn = this.confirmBtnTarget
      btn.textContent = flagged ? "Clear flag" : "Flag this place"
      btn.className = `flex-1 py-3.5 text-sm font-semibold active:bg-slate-50 ${flagged ? "text-slate-700" : "text-amber-600"}`
    }
  }
}
